import React, { useState } from 'react';
import { Button, Popover, PopoverProps, Steps } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import TargetA from '../../../../../../../../../images/target-type-A.svg';
import TargetAB from '../../../../../../../../../images/target-type-AB.svg';
import TargetABC from '../../../../../../../../../images/target-type-ABC.svg';
import TargetABCD from '../../../../../../../../../images/target-type-ABCD.svg';
import { TARGET_LEGEND_COMPETITION_HALF } from '../../../utils/constants';
import {
  getGameLegendTournaments,
  getTargetLegends,
  getIsTargetsSettingEnabled,
} from '../../../redux/selectors';
import './index.less';
import { generateColor } from '../../../utils';

interface SetProjectTargetPopoverProps extends PopoverProps {
  isReselectedItem: boolean;
  onFinishPicker?: (value: {
    targetNumChildScope: ('A' | 'B' | 'C' | 'D')[];
    archeryTournamentRoundSettings: {
      roundNumber: number;
      roundName: string;
      isQualifying: boolean;
      competitionHalf: string;
      categoryName: string;
      optionName: string;
      teamPlayerCount: number;
    };
  }) => void;
  onDelete?: () => void;
}

const SetProjectTargetPopover: React.FC<SetProjectTargetPopoverProps> = ({
  visible,
  trigger = 'click',
  isReselectedItem = false,
  onVisibleChange,
  onFinishPicker,
  onDelete,
  children,
}) => {
  const legends = useSelector(getGameLegendTournaments);
  const targetLegends = useSelector(getTargetLegends);
  const isTargetsSettingEnabled = useSelector(getIsTargetsSettingEnabled);
  const [current, setCurrent] = useState(0);

  const [selectedTournament, setSelectedTournament] = useState<any>();
  const [round, setRound] = useState<any>();

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const onFinishPickerInternal = (type: 'A' | 'AB' | 'ABC' | 'ABCD') => {
    onVisibleChange && onVisibleChange(false);
    onFinishPicker &&
      onFinishPicker({
        targetNumChildScope: type.split('') as ('A' | 'B' | 'C' | 'D')[],
        archeryTournamentRoundSettings: {
          roundNumber: round?.roundNumber,
          roundName: round?.roundName,
          isQualifying: round?.isQualifying,
          competitionHalf: round?.competitionHalf,
          categoryName: selectedTournament?.categoryName,
          optionName: selectedTournament?.optionName,
          teamPlayerCount: selectedTournament?.teamPlayerCount,
        },
      });
  };

  const steps = [
    {
      title: '选择项目',
      description:
        selectedTournament?.categoryName + selectedTournament?.optionName,
      content: (
        <div className="SetProjectTargetPopoverProject">
          {legends.map((i: any, index: number) => {
            const backgroundColor = generateColor(index);
            return (
              <Button
                className="SetProjectTargetPopoverProjectStageButton"
                style={{ background: backgroundColor }}
                key={`SetProjectTargetPopoverProject_SelectItems_${index}`}
                onClick={() => {
                  setSelectedTournament(i);
                  next();
                }}
              >
                {`${i.categoryName}${i.optionName}`}
              </Button>
            );
          })}
        </div>
      ),
    },
    {
      title: '选择阶段',
      description: round?.roundName,
      content: (
        <div className="SetProjectTargetPopoverProject">
          {selectedTournament?.rounds.map((i: any, index: number) => {
            const { competitionHalf, roundName: currentRoundName } = i;
            const {
              categoryName: selectedCategoryName,
              optionName: selectedOptionName,
            } = selectedTournament;
            const isDisabled = targetLegends.find((legend: any) => {
              const {
                categoryName: legendCategoryName,
                optionName: legendOptionName,
              } = legend;

              const selectedLegend = legend?.rounds.find((round: any) => {
                const {
                  roundName: legendRoundName,
                  competitionHalf: legendCompetitionHalf,
                  isSelected,
                } = round;

                return (
                  selectedCategoryName === legendCategoryName &&
                  selectedOptionName === legendOptionName &&
                  currentRoundName === legendRoundName &&
                  competitionHalf === legendCompetitionHalf &&
                  isSelected
                );
              });

              return selectedLegend;
            });

            return (
              <Button
                key={`SetProjectTargetPopoverProject_Selected_Stage_${index}`}
                onClick={() => {
                  setRound(i);
                  next();
                }}
                disabled={isDisabled}
              >
                {`${currentRoundName}${
                  competitionHalf
                    ? `(${
                        competitionHalf ===
                        TARGET_LEGEND_COMPETITION_HALF.FIRSTHALF
                          ? '上'
                          : '下'
                      })`
                    : ''
                }`}
              </Button>
            );
          })}
        </div>
      ),
    },
    {
      title: '选择靶型',
      content: (
        <div className="SetProjectTargetPopoverTarget">
          <div
            className="SetProjectTargetPopoverTargetItem"
            onClick={() => onFinishPickerInternal('A')}
          >
            <img
              className="SetProjectTargetPopoverTargetItemImage"
              src={TargetA}
              alt=""
            />
            <div className="SetProjectTargetPopoverTargetItemTitle">{`A型`}</div>
          </div>
          <div
            className="SetProjectTargetPopoverTargetItem"
            onClick={() => onFinishPickerInternal('AB')}
          >
            <img
              className="SetProjectTargetPopoverTargetItemImage"
              src={TargetAB}
              alt=""
            />
            <div className="SetProjectTargetPopoverTargetItemTitle">{`AB型`}</div>
          </div>
          <div
            className="SetProjectTargetPopoverTargetItem"
            onClick={() => onFinishPickerInternal('ABC')}
          >
            <img
              className="SetProjectTargetPopoverTargetItemImage"
              src={TargetABC}
              alt=""
            />
            <div className="SetProjectTargetPopoverTargetItemTitle">{`ABC型`}</div>
          </div>
          <div
            className="SetProjectTargetPopoverTargetItem"
            onClick={() => onFinishPickerInternal('ABCD')}
          >
            <img
              className="SetProjectTargetPopoverTargetItemImage"
              src={TargetABCD}
              alt=""
            />
            <div className="SetProjectTargetPopoverTargetItemTitle">{`ABCD型`}</div>
          </div>
        </div>
      ),
    },
  ];

  return isTargetsSettingEnabled ? (
    <Popover
      zIndex={2000}
      visible={visible}
      trigger={trigger}
      destroyTooltipOnHide
      onVisibleChange={onVisibleChange}
      afterVisibleChange={(visible) => {
        !visible &&
          setTimeout(() => {
            setCurrent(0);
            setSelectedTournament(undefined);
            setRound(undefined);
          }, 500);
      }}
      content={
        <div>
          <Steps current={current} onChange={setCurrent}>
            {steps.map((item, index) => (
              <Steps.Step
                key={`SetProjectTargetPopover_Steps_${index}`}
                title={item.title}
                description={item.description}
              />
            ))}
          </Steps>
          {steps[current].content}

          {isReselectedItem ? (
            <Button block danger onClick={onDelete}>
              <DeleteOutlined />
              <span>取消当前靶位的设置</span>
            </Button>
          ) : null}
        </div>
      }
    >
      {children}
    </Popover>
  ) : (
    <>{children}</>
  );
};

export default SetProjectTargetPopover;
